import { Component, OnInit } from '@angular/core';
import { PayrollResult } from './payroll.types';
import { PayrollCycle } from './payroll.types';
import { PayrollService } from '../services/payroll.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DataserviceService } from '../dataservice.service';
import { saveAs } from 'file-saver';
import { CustomerHistory } from './payroll.types';
import { AppComponent } from '../app.component';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-payroll',
  templateUrl: './payroll.component.html',
  styleUrl: './payroll.component.css'
})
export class PayrollComponent implements OnInit {
  payplanId: string | null = null;
  dateRange: Date[] = [];
  payDate: Date | null = null;
  calculating: boolean = false;
  results: PayrollResult[] = [];
  groupedPayrollHistory: any[] = [];
  showResults: boolean = false;
  cycle_holder_startDate: Date | null = null;
  cycle_holder_endDate: Date | null = null;
  showCustomerHistoryModal: boolean = false;
  selectedCustomerName: string = '';
  customerHistory: CustomerHistory[] = [];
  
  // Customer pay history breakdown modal
  showCustomerPayHistoryModal: boolean = false;
  selectedCustomerPayHistory: any[] = [];
  selectedCustomerForPayHistory: string = '';
  
  isLoading: boolean = false;
  loadingMessage: string = '';
  loadingTimeout: any;
  
  // Loading state for view details button
  loadingGroupDetails: string | null = null; // Store the group ID that's currently loading

  // Manager deduction UI state
  showDeductModal: boolean = false;
  deductUserId: string | null = null;
  deductAmount: number | null = null;
  deductReason: string = '';
  deductNotes: string = '';

  constructor(
    private payrollService: PayrollService,
    private dataService: DataserviceService,
    private message: NzMessageService,
    private app: AppComponent,
    private modal: NzModalService
  ) { }

  ngOnInit() {
    this.loadPayrollHistory();
    this.loadingHelper(true, '')
  }

  // Roles: Admin, Super Admin, Payroll Manager
  canManageHoldbacks(): boolean {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.super_admin) return true; // Super Admin
    const roleName = (user?.role?.name || '').toLowerCase();
    return roleName === 'admin' || roleName === 'payroll manager';
  }

  isMe(): boolean {
    // console.log('isMe');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // console.log('user here:',user);
    // Prefer a stable identifier you control. Examples:
    return user?.user_id === 1;
    // or:
    // return (user?.email || '').toLowerCase() === 'restaguy@gmail.com';
  }

  openDeductModal(userId: string) {
    if (!this.canManageHoldbacks()) return;
    this.deductUserId = userId;
    this.deductAmount = null;
    this.deductReason = '';
    this.deductNotes = '';
    this.showDeductModal = true;
  }

  submitDeduction() {
    if (!this.deductUserId || !this.deductAmount || this.deductAmount <= 0) {
      this.message.error('Enter a valid amount');
      return;
    }
    this.dataService.deductHoldback(this.deductUserId, this.deductAmount, this.deductReason, this.deductNotes)
      .subscribe({
        next: () => {
          this.message.success('Holdback deduction applied');
          this.showDeductModal = false;
        },
        error: (err) => {
          this.message.error(err?.error?.message || 'Failed to deduct');
        }
      })
  }

  loadingHelper(onOff: boolean, message?: string) {
    if (onOff) {
      this.loadingMessage = message || 'Loading...';
      this.isLoading = true;

      clearTimeout(this.loadingTimeout);

      this.loadingTimeout = setTimeout(() => {
        this.loadingMessage += ' This could take a while...';
      }, 5000);
    } else {
      clearTimeout(this.loadingTimeout);
      this.isLoading = false;
    }
  }

  calculatePayroll() {
    this.loadingHelper(true, 'Calculating payroll...')
    if (!this.dateRange[0] || !this.dateRange[1]) {
      this.message.error('Please select payplan and date range');
      return;
    }

    this.calculating = true;
    this.dataService.calculatePayroll(
      this.dateRange[0],
      this.dateRange[1]
    ).subscribe({
      next: (response) => {
        // Sort customers by name within each user result
        this.results = response.results.map((userResult: any) => ({
          ...userResult,
          customers: userResult.customers.sort((a: any, b: any) => 
            a.customerName.localeCompare(b.customerName)
          )
        }));
        this.showResults = true;
        this.calculating = false;
        this.loadingHelper(false)
      },
      error: (error) => {
        this.message.error('Failed to calculate payroll');
        this.calculating = false;
        this.loadingHelper(false)
      }
    });
  }

  savePayrollCycle() {
    this.loadingHelper(true, 'Saving payroll cycle...')
    if (!this.payDate) {
      this.message.error('Please select a pay date');
      return;
    }

    const cycle: Partial<PayrollCycle> = {
      payplanId: this.payplanId!,
      startDate: this.dateRange[0],
      endDate: this.dateRange[1],
      payDate: this.payDate,
      status: 'pending',
      results: this.results,
      totalAmount: this.getGrandTotal(),
      createdAt: new Date()
    };

    console.log('Frontend save cycle payload:', cycle);
    this.dataService.savePayrollCycle(cycle).subscribe({
      next: (savedCycle) => {
        this.message.success('Payroll cycle saved');
        this.loadPayrollHistory();
        this.loadingHelper(false)
      },
      error: (error) => {
        console.error('savePayrollCycle error:', error);
        this.message.error('Failed to save payroll cycle');
        this.loadingHelper(false)
      }
    });
  }

  markAsPaid(cycleId: string) {
    this.dataService.markPayrollCycleAsPaid(cycleId).subscribe({
      next: () => {
        this.message.success('Marked as paid');
        this.loadPayrollHistory();
      },
      error: (error) => {
        this.message.error('Failed to mark as paid');
      }
    });
  }

  markAsUnpaid(cycleId: string) {
    this.dataService.markPayrollCycleAsUnpaid(cycleId).subscribe({
      next: () => {
        this.message.success('Reverted to pending');
        this.loadPayrollHistory();
      },
      error: () => this.message.error('Failed to revert cycle')
    });
  }

  deletePendingCycle(cycleId: string) {
    this.modal.confirm({
      nzTitle: 'Delete pending payroll?',
      nzContent: 'This will remove the pending cycle and its pending commissions.',
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzOnOk: () => {
        this.dataService.deleteData(`payplans/cycles/${cycleId}`).subscribe({
          next: () => {
            this.message.success('Pending payroll deleted');
            this.loadPayrollHistory();
          },
          error: (err) => {
            this.message.error(err?.error?.message || 'Failed to delete pending payroll');
          }
        });
      }
    });
  }

  loadPayrollHistory() {
    this.loadingHelper(true, 'Loading payroll history...')
    this.dataService.getGroupedPayrollHistory().subscribe({
      next: (groupedHistory) => {
        this.groupedPayrollHistory = groupedHistory;
        this.loadingHelper(false)
      },
      error: (error) => {
        this.message.error('Failed to load payroll history');
        this.loadingHelper(false)
      }
    });
  }



  getGroupStatusColor(status: string): string {
    switch (status) {
      case 'paid': return 'green';
      case 'pending': return 'gold';
      case 'mixed': return 'orange';
      default: return 'default';
    }
  }


  markGroupAsPaid(group: any) {
    // Mark all cycles in the group as paid
    const promises = group.cycles.map((cycle: any) => 
      this.dataService.markPayrollCycleAsPaid(cycle.id).toPromise()
    );

    Promise.all(promises).then(() => {
      this.message.success('All cycles marked as paid');
      this.loadPayrollHistory();
    }).catch(() => {
      this.message.error('Failed to mark some cycles as paid');
      this.loadPayrollHistory(); // Reload to show current state
    });
  }

  markGroupAsUnpaid(group: any) {
    // Mark all cycles in the group as unpaid
    const promises = group.cycles.map((cycle: any) => 
      this.dataService.markPayrollCycleAsUnpaid(cycle.id).toPromise()
    );

    Promise.all(promises).then(() => {
      this.message.success('All cycles reverted to pending');
      this.loadPayrollHistory();
    }).catch(() => {
      this.message.error('Failed to revert some cycles');
      this.loadPayrollHistory(); // Reload to show current state
    });
  }

  viewGroupDetails(group: any) {
    // Set loading state for this specific group
    this.loadingGroupDetails = group.id;
    
    // Show combined details from all cycles in the group using backend-processed data
    this.loadingHelper(true, 'Loading combined cycle details...');
    
    console.log('=== VIEW DETAILS DRAWER DATA (Customer Commissions Based) ===');
    console.log('Group data received:', group);
    console.log('Payees in group:', group.payees);
    console.log('Number of payees:', group.payees?.length || 0);
    
    // Show payee vs sales rep differences
    group.payees?.forEach((payee: any, index: number) => {
      console.log(`Payee ${index + 1}:`);
      console.log(`  - Payee (who gets paid): ${payee.payeeName}`);
      console.log(`  - Original Sales Rep: ${payee.salesRep}`);
      console.log(`  - Amount: $${payee.totalCommission}`);
      console.log(`  - Same person?: ${payee.payeeName === payee.salesRep ? 'Yes' : 'No'}`);
    });
    
    // Convert payees data to the expected format for the drawer
    this.results = group.payees.map((payee: any) => ({
      userId: payee.payeeId,
      userName: payee.payeeName,
      payplanName: payee.payplanName,
      totalCommission: payee.totalCommission,
      totalHoldbacks: payee.totalHoldbacks || 0,
      totalAdjustments: payee.totalAdjustments || 0,
      netPay: payee.netPay || payee.totalCommission, // Fallback to totalCommission if netPay not available
      // Individual customer details with enhanced data
      customers: (payee.customers || []).map((customer: any) => ({
          customerId: customer.customerId || `customer-${Math.random()}`,
          customerName: customer.customerName || 'Unknown Customer',
          commission: Number(customer.commission || 0),
          originalCommission: Number(customer.commission || 0),
          wasAdjusted: customer.isAdjustment || false,
          ruleDescription: customer.ruleDescription || `Sales Rep: ${customer.originalSalesRep}`,
          totalReceived: Number(customer.commission || 0),
          totalPaidSoFar: Number(customer.totalPaidAllTime || 0), // NEW: Total paid all time
          remainingAvailable: Number(customer.commission || 0),
          fiberPayRecordId: customer.fiberPayRecordId || '',
          workOrderId: customer.workOrderId || '',
          paymentDate: new Date(),
          providerAccountNumber: '',
          product: customer.payType || 'commission', // NEW: Use pay type instead of payplan name
          isSummary: false,
          salesRep: customer.originalSalesRep, // NEW: Use original sales rep
          isAdjustment: customer.isAdjustment,
          isCompanyPayment: customer.isCompanyPayment,
          // NEW: Enhanced fields
          totalPaidAllTime: Number(customer.totalPaidAllTime || 0),
          originalSalesRep: customer.originalSalesRep,
          payType: customer.payType || 'commission',
          status: customer.status || 'pending'
        }))
    }));
    
    console.log('Processed results for drawer:', this.results);
    console.log('Results structure sample:', this.results[0]);
    
    this.showResults = true;
    
    // Set the date range for the drawer title
    const allStartDates = group.cycles.map((c: any) => new Date(c.startDate));
    const allEndDates = group.cycles.map((c: any) => new Date(c.endDate));
    this.cycle_holder_startDate = new Date(Math.min(...allStartDates.map((d: Date) => d.getTime())));
    this.cycle_holder_endDate = new Date(Math.max(...allEndDates.map((d: Date) => d.getTime())));
    
    this.loadingHelper(false);
    this.loadingGroupDetails = null; // Clear loading state
  }

  getPayTypeDisplay(payType: string): string {
    switch (payType?.toLowerCase()) {
      case 'commission':
        return 'Commission';
      case 'override':
        return 'Override';
      case 'adjustment':
        return 'Adjustment';
      default:
        return 'Commission';
    }
  }

  getPayTypeColor(payType: string): string {
    switch (payType?.toLowerCase()) {
      case 'commission':
        return 'blue';
      case 'override':
        return 'green';
      case 'adjustment':
        return 'orange';
      default:
        return 'blue';
    }
  }

  showCustomerPayHistoryBreakdown(customerId: string, customerName: string) {
    this.selectedCustomerForPayHistory = customerName;
    this.showCustomerPayHistoryModal = true;
    this.loadCustomerPayHistory(customerId);
  }

  closeCustomerPayHistory() {
    this.showCustomerPayHistoryModal = false;
    this.selectedCustomerPayHistory = [];
    this.selectedCustomerForPayHistory = '';
  }

  private loadCustomerPayHistory(customerId: string) {
    this.loadingHelper(true, 'Loading payment history...');
    
    this.dataService.getCustomerPayHistory(customerId).subscribe({
      next: (history) => {
        console.log('Loaded customer pay history:', history);
        this.selectedCustomerPayHistory = history;
        this.loadingHelper(false);
      },
      error: (error) => {
        console.error('Failed to load customer pay history:', error);
        this.message.error('Failed to load payment history');
        this.loadingHelper(false);
      }
    });
  }

  getCustomerPayHistoryTotal(): number {
    return this.selectedCustomerPayHistory.reduce((sum, payment) => sum + payment.amount, 0);
  }

  isGroupLoading(groupId: string): boolean {
    return this.loadingGroupDetails === groupId;
  }

  exportGroupToCSV(group: any) {
    // Use payees data for export
    if (!group.payees || group.payees.length === 0) {
      this.message.warning('No data to export');
      return;
    }

    // Convert payees data to results format for export
    const exportResults = group.payees.map((payee: any) => ({
      userId: payee.payeeId,
      userName: payee.payeeName,
      payplanName: payee.payplanName,
      totalCommission: payee.totalCommission,
      customers: payee.customers
    }));

    // Create a temporary cycle-like object for export
    const tempCycle = {
      results: exportResults,
      startDate: group.payDate,
      endDate: group.payDate,
      payDate: group.payDate
    } as any;

    this.exportToCSV(tempCycle);
  }

  deleteGroupCycles(group: any) {
    const pendingCycles = group.cycles.filter((cycle: any) => cycle.status === 'pending');
    
    if (pendingCycles.length === 0) {
      this.message.warning('No pending cycles to delete');
      return;
    }

    this.modal.confirm({
      nzTitle: `Delete ${pendingCycles.length} pending payroll cycle(s)?`,
      nzContent: 'This will remove the pending cycles and their pending commissions.',
      nzOkText: 'Delete',
      nzOkDanger: true,
      nzOnOk: () => {
        const promises = pendingCycles.map((cycle: any) => 
          this.dataService.deleteData(`payplans/cycles/${cycle.id}`).toPromise()
        );

        Promise.all(promises).then(() => {
          this.message.success(`${pendingCycles.length} pending cycle(s) deleted`);
          this.loadPayrollHistory();
        }).catch((err) => {
          this.message.error('Failed to delete some cycles');
          this.loadPayrollHistory(); // Reload to show current state
        });
      }
    });
  }

  getGrandTotal(): number {
    return this.results.reduce((total, userResult) =>
      total + userResult.totalCommission, 0);
  }

  viewCycleDetails(cycle: PayrollCycle) {
    this.loadingHelper(true, 'Loading cycle details...');
    
    this.dataService.getData(`payplans/cycle-details/${cycle.id}`).subscribe({
      next: (enrichedCycle: any) => {
        // Sort customers by name within each user result
        this.results = enrichedCycle.results.map((userResult: any) => ({
          ...userResult,
          customers: userResult.customers.sort((a: any, b: any) => 
            a.customerName.localeCompare(b.customerName)
          )
        }));
        
        this.showResults = true;
        this.cycle_holder_startDate = enrichedCycle.startDate;
        this.cycle_holder_endDate = enrichedCycle.endDate;
        this.loadingHelper(false);
      },
      error: (error) => {
        console.error('Failed to load cycle details:', error);
        this.message.error('Failed to load cycle details');
        this.loadingHelper(false);
      }
    });
  }

  exportToCSV(cycle?: PayrollCycle) {
    const dataToExport = cycle?.results || this.results;
    if (!dataToExport.length) {
      this.message.warning('No data to export');
      return;
    }

    // Helper function to escape CSV fields
    const escapeCsvField = (field: any): string => {
      const stringField = String(field);
      // If field contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
      if (stringField.includes('"') || stringField.includes(',') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    const csvRows = [];
    // Header row with enhanced fields
    csvRows.push(['Sales Rep', 'Customer', 'Commission', 'Total Paid All Time', 'Original Sales Rep', 'Pay Type', 'Status'].map(escapeCsvField));

    // Data rows with enhanced fields
    dataToExport.forEach(userResult => {
      userResult.customers.forEach(customer => {
        csvRows.push([
          escapeCsvField(userResult.userName),
          escapeCsvField(customer.customerName),
          escapeCsvField(customer.commission.toFixed(2)),
          escapeCsvField(((customer as any).totalPaidAllTime || 0).toFixed(2)),
          escapeCsvField((customer as any).originalSalesRep || (customer as any).salesRep),
          escapeCsvField(this.getPayTypeDisplay((customer as any).payType)),
          escapeCsvField((customer as any).status || 'pending')
        ]);
      });
      // Add a subtotal row for each sales rep
      csvRows.push([
        escapeCsvField(`${userResult.userName} Total`),
        escapeCsvField(''),
        escapeCsvField(userResult.totalCommission.toFixed(2)),
        escapeCsvField(''),
        escapeCsvField(''),
        escapeCsvField(''),
        escapeCsvField('')
      ]);
      csvRows.push([]); // Empty row for separation
    });

    // Add grand total
    const grandTotal = dataToExport.reduce((total, user) => total + user.totalCommission, 0);
    csvRows.push([
      escapeCsvField('Grand Total'),
      escapeCsvField(''),
      escapeCsvField(grandTotal.toFixed(2)),
      escapeCsvField(''),
      escapeCsvField(''),
      escapeCsvField(''),
      escapeCsvField('')
    ]);

    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

    // Generate filename with date range if available
    let dateStr;
    if (cycle) {
      const startDate = cycle.startDate instanceof Date ?
        cycle.startDate.toISOString().split('T')[0] :
        new Date(cycle.startDate).toISOString().split('T')[0];
      const endDate = cycle.endDate instanceof Date ?
        cycle.endDate.toISOString().split('T')[0] :
        new Date(cycle.endDate).toISOString().split('T')[0];
      dateStr = `${startDate}_${endDate}`;
    } else {
      dateStr = new Date().toISOString().split('T')[0];
    }

    saveAs(blob, `payroll_export_${dateStr}.csv`);
  }

  showCustomerHistory(customerId: string) {
    const customer = this.findCustomer(customerId);
    if (customer) {
      this.selectedCustomerName = customer.customerName;
      this.showCustomerHistoryModal = true;
      this.loadCustomerHistory(customerId);
    }
  }

  closeCustomerHistory() {
    this.showCustomerHistoryModal = false;
    this.customerHistory = [];
  }

  private findCustomer(customerId: string) {
    for (const userResult of this.results) {
      const customer = userResult.customers.find(c => c.customerId === customerId);
      if (customer) return customer;
    }
    return null;
  }

  private loadCustomerHistory(customerId: string) {
    this.dataService.getCustomerCommissionHistory(customerId).subscribe({
      next: (history) => {
        console.log('Loaded customer history:', history);
        
        // Add the current pending commission if it exists
        const currentCustomer = this.findCustomer(customerId);
        if (currentCustomer && currentCustomer.commission > 0) {
          const pendingCommission = {
            id: `pending-${currentCustomer.fiberPayRecordId}`,
            createdAt: null,
            datePaid: null,
            amount: currentCustomer.commission,
            fiberPayAmount: currentCustomer.totalReceived,
            userName: this.results.find(r => r.customers.some(c => c.customerId === customerId))?.userName || 'Unknown',
            cycleDateRange: 'Current Calculation',
            status: 'Pending' as const,
            workOrderId: currentCustomer.workOrderId,
            paymentDate: currentCustomer.paymentDate,
            orderType: 'Current'
          };
          
          // Add pending commission at the top
          this.customerHistory = [pendingCommission, ...history];
        } else {
          this.customerHistory = history;
        }
      },
      error: (error) => {
        console.error('Error loading customer history:', error);
        this.message.error('Failed to load customer history');
      }
    });
  }
}
